// pages/EstadisticasFacturacion.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, Button, Select, Space, Typography, message, Tag,
  Tooltip, Row, Col, Badge, Card, DatePicker, Grid,
  Drawer, FloatButton, Segmented, Empty, Progress, Avatar, List,
  Divider, Input, Switch
} from 'antd';
import {
  BarChartOutlined, PieChartOutlined, AreaChartOutlined,
  ReloadOutlined, FilterOutlined, SearchOutlined, DownloadOutlined,
  DollarOutlined, ShoppingOutlined, CalendarOutlined, TeamOutlined,
  AppstoreOutlined, ArrowUpOutlined, ArrowDownOutlined,
  PercentageOutlined, TrophyOutlined, FireOutlined, ThunderboltOutlined,
  ExperimentOutlined, FundOutlined, DotChartOutlined, ShopOutlined,
  TagsOutlined, GlobalOutlined, ClearOutlined, RiseOutlined,
  FallOutlined, LineChartOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from '../utils/dayjsConfig';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  Legend, ComposedChart, Treemap
} from 'recharts';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

// 🎨 Paleta premium
const COLORS = [
  '#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1',
  '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'
];

const formatEuro = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0,00 €';
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(value);
};

const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(value);
};

// ============ KPI CARD ============
const KPICard = ({ title, value, icon, color = '#1890ff', subtitle, trend, trendValue, isMobile }) => {
  const isPositive = trend === 'up';
  return (
    <Card
      bordered={false}
      styles={{ body: { padding: isMobile ? 14 : 20 } }}
      style={{
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        borderLeft: `4px solid ${color}`,
        borderRadius: 12,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
      hoverable
    >
      <div style={{
        position: 'absolute', right: -20, top: -20, opacity: 0.08,
        fontSize: isMobile ? 80 : 120, color, pointerEvents: 'none',
      }}>
        {icon}
      </div>

      <Space direction="vertical" size={4} style={{ width: '100%', position: 'relative', zIndex: 1 }}>
        <Space size={6}>
          <span style={{ color, fontSize: isMobile ? 14 : 16 }}>{icon}</span>
          <Text type="secondary" style={{
            fontSize: isMobile ? 11 : 12, textTransform: 'uppercase',
            letterSpacing: 0.5, fontWeight: 600,
          }}>
            {title}
          </Text>
        </Space>

        <span style={{
          fontSize: isMobile ? 20 : 26,
          fontWeight: 700, color: '#1f1f1f', lineHeight: 1.1,
        }}>
          {value}
        </span>

        {subtitle && (
          <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>{subtitle}</Text>
        )}

        {trend && (
          <Space size={4} style={{ marginTop: 4 }}>
            {isPositive
              ? <ArrowUpOutlined style={{ color: '#52c41a', fontSize: 12 }} />
              : <ArrowDownOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />}
            <Text style={{
              color: isPositive ? '#52c41a' : '#ff4d4f',
              fontSize: 12, fontWeight: 600,
            }}>
              {trendValue}
            </Text>
          </Space>
        )}
      </Space>
    </Card>
  );
};

const EstadisticasFacturacion = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filtrosDrawerVisible, setFiltrosDrawerVisible] = useState(false);

  const [filtros, setFiltros] = useState({
    fechaDesde: dayjs().startOf('month'),
    fechaHasta: dayjs(),
    agencia: null,
    serie: [],
    subagencia: null,
    texto: '',
  });

  const [agrupacion, setAgrupacion] = useState('agencia');

  const [agencias, setAgencias] = useState([]);
  const [series, setSeries] = useState([]);

  // ============ CARGA INICIAL (solo una vez) ============
  useEffect(() => {
    fetchAgencias();
    fetchSeries();
    fetchEstadisticas();
  }, []);

  const fetchAgencias = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/agencias`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencias(res.data.data || []);
    } catch (e) { console.error('Error agencias:', e); }
  };

  const fetchSeries = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/series`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeries(res.data.data || []);
    } catch (e) { console.error('Error series:', e); }
  };

  // Carga SIN filtros al backend — todo se filtra en cliente para que sea instantáneo
  const fetchEstadisticas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/estadisticas/facturacion`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const raw = res.data.data || res.data || [];
      setData(Array.isArray(raw) ? raw : []);
    } catch (error) {
      console.error('❌ Error estadísticas:', error);
      message.error('Error al cargar las estadísticas ');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // ============ 🎯 FILTRADO EN CLIENTE (afecta a TODO) ============
  const dataFiltrada = useMemo(() => {
    return data.filter(d => {
      // Fecha
      if (filtros.fechaDesde && dayjs(d.fecha).isBefore(dayjs(filtros.fechaDesde).startOf('day'))) return false;
      if (filtros.fechaHasta && dayjs(d.fecha).isAfter(dayjs(filtros.fechaHasta).endOf('day'))) return false;

      // Agencia
      if (filtros.agencia && d.agencia !== filtros.agencia) return false;

      // Series (múltiple)
      if (filtros.serie?.length > 0 && !filtros.serie.includes(d.serie)) return false;

      // Subagencia
      if (filtros.subagencia && d.SubAgencia !== filtros.subagencia) return false;

      // Texto libre (agencia, concepto, subagencia)
      if (filtros.texto) {
        const t = filtros.texto.toLowerCase();
        const hay = (
          (d.nombreagencia || '').toLowerCase().includes(t) ||
          (d.descripcion || '').toLowerCase().includes(t) ||
          (d.nombresubagencia || '').toLowerCase().includes(t) ||
          (d.agencia || '').toLowerCase().includes(t) ||
          (d.codigo || '').toLowerCase().includes(t)
        );
        if (!hay) return false;
      }

      return true;
    });
  }, [data, filtros]);

  // ¿Hay filtros activos?
  const filtrosActivos = useMemo(() => {
    let n = 0;
    if (filtros.agencia) n++;
    if (filtros.serie?.length) n++;
    if (filtros.subagencia) n++;
    if (filtros.texto) n++;
    return n;
  }, [filtros]);

  // ============ KPIs (sobre datos filtrados) ============
  const kpis = useMemo(() => {
    if (!dataFiltrada.length) return {
      totalImporte: 0, totalUnidades: 0, numAgencias: 0, numSeries: 0,
      ticketMedio: 0, numRegistros: 0, numConceptos: 0,
      topAgencia: null, topConcepto: null, mejorDia: null,
    };

    const totalImporte = dataFiltrada.reduce((s, d) => s + (parseFloat(d.total) || 0), 0);
    const totalUnidades = dataFiltrada.reduce((s, d) => s + (parseFloat(d.unidades) || 0), 0);
    const numAgencias = new Set(dataFiltrada.map(d => d.agencia)).size;
    const numSeries = new Set(dataFiltrada.map(d => d.serie)).size;
    const numConceptos = new Set(dataFiltrada.map(d => d.codigo)).size;

    const agMap = {};
    dataFiltrada.forEach(d => {
      if (!agMap[d.agencia]) agMap[d.agencia] = { agencia: d.agencia, nombre: d.nombreagencia, total: 0 };
      agMap[d.agencia].total += parseFloat(d.total) || 0;
    });
    const topAgencia = Object.values(agMap).sort((a, b) => b.total - a.total)[0];

    const conMap = {};
    dataFiltrada.forEach(d => {
      if (!conMap[d.codigo]) conMap[d.codigo] = { codigo: d.codigo, descripcion: d.descripcion, total: 0 };
      conMap[d.codigo].total += parseFloat(d.total) || 0;
    });
    const topConcepto = Object.values(conMap).sort((a, b) => b.total - a.total)[0];

    const diaMap = {};
    dataFiltrada.forEach(d => {
      const dia = dayjs(d.fecha).format('YYYY-MM-DD');
      if (!diaMap[dia]) diaMap[dia] = { fecha: dia, total: 0 };
      diaMap[dia].total += parseFloat(d.total) || 0;
    });
    const mejorDia = Object.values(diaMap).sort((a, b) => b.total - a.total)[0];

    return {
      totalImporte, totalUnidades, numAgencias, numSeries,
      ticketMedio: totalUnidades > 0 ? totalImporte / totalUnidades : 0,
      numRegistros: dataFiltrada.length, numConceptos,
      topAgencia, topConcepto, mejorDia,
    };
  }, [dataFiltrada]);

  // ============ DATOS PARA GRÁFICOS (sobre datos filtrados) ============
  const chartData = useMemo(() => {
    if (!dataFiltrada.length) return {
      porAgencia: [], porSerie: [], porConcepto: [], porFecha: [], porSubagencia: []
    };

    const agMap = {};
    dataFiltrada.forEach(d => {
      if (!agMap[d.agencia]) agMap[d.agencia] = {
        name: d.agencia, fullName: d.nombreagencia,
        agencia: d.agencia, total: 0, unidades: 0, registros: 0,
      };
      agMap[d.agencia].total += parseFloat(d.total) || 0;
      agMap[d.agencia].unidades += parseFloat(d.unidades) || 0;
      agMap[d.agencia].registros += 1;
    });
    const porAgencia = Object.values(agMap).sort((a, b) => b.total - a.total);

    const serMap = {};
    dataFiltrada.forEach(d => {
      if (!serMap[d.serie]) serMap[d.serie] = {
        name: `Serie ${d.serie}`, serie: d.serie,
        total: 0, unidades: 0, registros: 0,
      };
      serMap[d.serie].total += parseFloat(d.total) || 0;
      serMap[d.serie].unidades += parseFloat(d.unidades) || 0;
      serMap[d.serie].registros += 1;
    });
    const porSerie = Object.values(serMap);

    const conMap = {};
    dataFiltrada.forEach(d => {
      if (!conMap[d.codigo]) conMap[d.codigo] = {
        name: d.descripcion, codigo: d.codigo, descripcion: d.descripcion,
        total: 0, unidades: 0, registros: 0,
      };
      conMap[d.codigo].total += parseFloat(d.total) || 0;
      conMap[d.codigo].unidades += parseFloat(d.unidades) || 0;
      conMap[d.codigo].registros += 1;
    });
    const porConcepto = Object.values(conMap).sort((a, b) => b.total - a.total);

    const fecMap = {};
    dataFiltrada.forEach(d => {
      const key = dayjs(d.fecha).format('YYYY-MM-DD');
      if (!fecMap[key]) fecMap[key] = { fecha: key, total: 0, unidades: 0, registros: 0 };
      fecMap[key].total += parseFloat(d.total) || 0;
      fecMap[key].unidades += parseFloat(d.unidades) || 0;
      fecMap[key].registros += 1;
    });
    const porFecha = Object.values(fecMap).sort((a, b) => a.fecha.localeCompare(b.fecha));

    const subMap = {};
    dataFiltrada.forEach(d => {
      const key = d.SubAgencia || 'SIN_SUB';
      if (!subMap[key]) subMap[key] = {
        name: d.nombresubagencia || d.SubAgencia || 'Sin Subagencia',
        subagencia: d.SubAgencia, total: 0, unidades: 0, registros: 0,
      };
      subMap[key].total += parseFloat(d.total) || 0;
      subMap[key].unidades += parseFloat(d.unidades) || 0;
      subMap[key].registros += 1;
    });
    const porSubagencia = Object.values(subMap).sort((a, b) => b.total - a.total);

    return { porAgencia, porSerie, porConcepto, porFecha, porSubagencia };
  }, [dataFiltrada]);

  // Lista de subagencias únicas (para el filtro)
  const subagenciasDisponibles = useMemo(() => {
    const s = new Set();
    data.forEach(d => { if (d.SubAgencia) s.add(d.SubAgencia); });
    return Array.from(s).sort();
  }, [data]);

  // ============ TABLA AGRUPADA (sobre datos filtrados) ============
  const tableData = useMemo(() => {
    if (!dataFiltrada.length) return [];
    const map = {};

    dataFiltrada.forEach(d => {
      let key, label;
      switch (agrupacion) {
        case 'agencia':
          key = d.agencia;
          label = `${d.agencia} - ${d.nombreagencia || ''}`;
          break;
        case 'serie':
          key = d.serie;
          label = `Serie ${d.serie}`;
          break;
        case 'codigo':
          key = d.codigo;
          label = `${d.codigo} - ${d.descripcion || ''}`;
          break;
        case 'fecha':
          key = dayjs(d.fecha).format('YYYY-MM-DD');
          label = dayjs(d.fecha).format('DD/MM/YYYY');
          break;
        case 'subagencia':
          key = d.SubAgencia || 'SIN_SUB';
          label = d.nombresubagencia || d.SubAgencia || 'Sin Subagencia';
          break;
        default:
          key = d.agencia;
          label = d.agencia;
      }

      if (!map[key]) {
        map[key] = {
          key, label,
          agencia: d.agencia, nombreagencia: d.nombreagencia,
          serie: d.serie, codigo: d.codigo, descripcion: d.descripcion,
          SubAgencia: d.SubAgencia, nombresubagencia: d.nombresubagencia,
          fecha: d.fecha, total: 0, unidades: 0, registros: 0,
        };
      }
      map[key].total += parseFloat(d.total) || 0;
      map[key].unidades += parseFloat(d.unidades) || 0;
      map[key].registros += 1;
    });

    const arr = Object.values(map).sort((a, b) => b.total - a.total);
    const grandTotal = arr.reduce((s, r) => s + r.total, 0);
    return arr.map(r => ({
      ...r,
      ticketMedio: r.unidades > 0 ? r.total / r.unidades : 0,
      porcentaje: grandTotal > 0 ? (r.total / grandTotal) * 100 : 0,
    }));
  }, [dataFiltrada, agrupacion]);

  // ============ COLUMNAS TABLA ============
  const tableColumns = useMemo(() => {
    const cols = [];
    const firstColMap = {
      agencia: {
        title: 'Agencia', width: isMobile ? 160 : 280,
        render: (v, r) => (
          <Space>
            <Avatar size="small" style={{ backgroundColor: COLORS[0] }}>
              {r.agencia?.substring(0, 2)}
            </Avatar>
            <div>
              <Text strong style={{ fontSize: isMobile ? 12 : 13 }}>{r.agencia}</Text>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.nombreagencia}</div>
            </div>
          </Space>
        ),
      },
      serie: {
        title: 'Serie', width: 140,
        render: (v, r) => (
          <Tag color="blue" style={{ fontWeight: 600, fontSize: 13, padding: '4px 10px' }}>
            {r.serie}
          </Tag>
        ),
      },
      codigo: {
        title: 'Concepto', width: isMobile ? 180 : 300,
        render: (v, r) => (
          <div>
            <Tag color="purple" style={{ fontWeight: 600 }}>{r.codigo}</Tag>
            <Text style={{ fontSize: isMobile ? 12 : 13 }}>{r.descripcion}</Text>
          </div>
        ),
      },
      fecha: {
        title: 'Fecha', width: 140,
        render: (v) => (
          <Space>
            <CalendarOutlined style={{ color: '#1890ff' }} />
            <Text strong>{v}</Text>
          </Space>
        ),
      },
      subagencia: {
        title: 'Subagencia', width: isMobile ? 180 : 260,
        render: (v, r) => (
          <div>
            <Text strong style={{ fontSize: isMobile ? 12 : 13 }}>{r.SubAgencia || '—'}</Text>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.nombresubagencia}</div>
          </div>
        ),
      },
    };

    const first = firstColMap[agrupacion];
    cols.push({
      title: first.title, dataIndex: 'label', key: 'label',
      fixed: isMobile ? false : 'left', width: first.width,
      render: first.render,
    });

    cols.push(
      {
        title: 'Total', dataIndex: 'total', key: 'total',
        align: 'right', width: isMobile ? 110 : 140,
        sorter: (a, b) => a.total - b.total,
        defaultSortOrder: 'descend',
        render: (v) => (
          <Text strong style={{
            color: v < 0 ? '#ff4d4f' : '#52c41a',
            fontSize: isMobile ? 12 : 14,
          }}>
            {formatEuro(v)}
          </Text>
        ),
      },
      {
        title: 'Unidades', dataIndex: 'unidades', key: 'unidades',
        align: 'right', width: isMobile ? 80 : 100,
        sorter: (a, b) => a.unidades - b.unidades,
        render: (v) => <Text>{formatNumber(v)}</Text>,
      },
      {
        title: 'Factura media', dataIndex: 'ticketMedio', key: 'ticketMedio',
        align: 'right', width: isMobile ? 100 : 120,
        responsive: ['md'],
        render: (v) => <Text type="secondary">{formatEuro(v)}</Text>,
      },
      {
        title: '% Total', dataIndex: 'porcentaje', key: 'porcentaje',
        width: isMobile ? 90 : 160,
        sorter: (a, b) => a.porcentaje - b.porcentaje,
        render: (v) => (
          <Tooltip title={`${v.toFixed(2)}% del total`}>
            <Progress
              percent={Math.min(Math.abs(v), 100)}
              size="small"
              strokeColor={v < 0 ? '#ff4d4f' : '#1890ff'}
              format={() => `${v.toFixed(1)}%`}
            />
          </Tooltip>
        ),
      },
      {
        title: 'Registros', dataIndex: 'registros', key: 'registros',
        align: 'center', width: 90, responsive: ['lg'],
        render: (v) => <Badge count={v} style={{ backgroundColor: '#722ed1' }} />,
      }
    );

    return cols;
  }, [agrupacion, isMobile]);

  // ============ 🎯 PANEL DE FILTROS (SIEMPRE ARRIBA) ============
  const filtrosBar = (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        borderRadius: 12,
        background: 'linear-gradient(135deg, #f0f5ff 0%, #ffffff 100%)',
        border: '1px solid #e6f0ff',
        boxShadow: '0 2px 8px rgba(24,144,255,0.06)',
      }}
      styles={{ body: { padding: isMobile ? 12 : 16 } }}
    >
      <Row gutter={[12, 12]} align="middle">
        {/* Buscador de texto libre */}
        <Col xs={24} md={8} lg={6}>
          <Input
            placeholder="Buscar agencia, concepto, subagencia..."
            prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
            value={filtros.texto}
            onChange={(e) => setFiltros({ ...filtros, texto: e.target.value })}
            allowClear
            size={isMobile ? 'middle' : 'middle'}
          />
        </Col>

        {/* Fechas */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <RangePicker
            style={{ width: '100%' }}
            value={[filtros.fechaDesde, filtros.fechaHasta]}
            onChange={(dates) => setFiltros({
              ...filtros,
              fechaDesde: dates?.[0] || null,
              fechaHasta: dates?.[1] || null,
            })}
            format="DD/MM/YYYY"
            size="middle"
            allowClear={false}
          />
        </Col>

        {/* Agencia */}
        <Col xs={24} sm={12} md={4} lg={3}>
          <Select
            style={{ width: '100%' }}
            placeholder="Agencia"
            value={filtros.agencia}
            onChange={(v) => setFiltros({ ...filtros, agencia: v })}
            allowClear showSearch
            filterOption={(input, option) =>
              (option.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {agencias.map(a => (
              <Select.Option key={a.codigo} value={a.codigo}>
                {a.codigo} - {a.nombre}
              </Select.Option>
            ))}
          </Select>
        </Col>

        {/* Serie múltiple */}
        <Col xs={24} sm={12} md={4} lg={3}>
          <Select
            style={{ width: '100%' }}
            mode="multiple"
            placeholder="Series"
            value={filtros.serie}
            onChange={(v) => setFiltros({ ...filtros, serie: v })}
            allowClear
            maxTagCount={1}
          >
            {series.map(s => (
              <Select.Option key={s.serie} value={s.serie}>{s.serie}</Select.Option>
            ))}
          </Select>
        </Col>

        {/* Subagencia */}
        <Col xs={24} sm={12} md={4} lg={3}>
          <Select
            style={{ width: '100%' }}
            placeholder="Subagencia"
            value={filtros.subagencia}
            onChange={(v) => setFiltros({ ...filtros, subagencia: v })}
            allowClear showSearch
          >
            {subagenciasDisponibles.map(s => (
              <Select.Option key={s} value={s}>{s}</Select.Option>
            ))}
          </Select>
        </Col>

        {/* Botones */}
        <Col xs={24} md={4} lg={3}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Tooltip title="Recargar datos">
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchEstadisticas}
                loading={loading}
              />
            </Tooltip>
            <Tooltip title="Limpiar filtros">
              <Button
                icon={<ClearOutlined />}
                onClick={() => setFiltros({
                  fechaDesde: dayjs().startOf('month'),
                  fechaHasta: dayjs(),
                  agencia: null,
                  serie: [],
                  subagencia: null,
                  texto: '',
                })}
                disabled={filtrosActivos === 0}
              />
            </Tooltip>
          </Space>
        </Col>
      </Row>

      {/* Fila secundaria: estado + agrupación */}
      <Divider style={{ margin: '12px 0 10px' }} />
      <Row gutter={[12, 10]} align="middle" justify="space-between">
        <Col xs={24} md={14}>
          <Space wrap size={6}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <FilterOutlined /> Mostrando:
            </Text>
            <Tag color="blue" style={{ fontWeight: 600 }}>
              {kpis.numRegistros} registros
            </Tag>
            <Tag color="green" style={{ fontWeight: 600 }}>
              {formatEuro(kpis.totalImporte)}
            </Tag>
            <Tag color="purple" style={{ fontWeight: 600 }}>
              {kpis.numAgencias} agencias
            </Tag>
            {filtrosActivos > 0 && (
              <Tag color="orange" style={{ fontWeight: 600 }}>
                {filtrosActivos} filtro{filtrosActivos > 1 ? 's' : ''} activo{filtrosActivos > 1 ? 's' : ''}
              </Tag>
            )}
          </Space>
        </Col>
        <Col xs={24} md={10}>
          <Space wrap style={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Agrupar por:</Text>
            <Segmented
              size="small"
              value={agrupacion}
              onChange={setAgrupacion}
              options={[
                { label: 'Agencia', value: 'agencia', icon: <ShopOutlined /> },
                { label: 'Serie', value: 'serie', icon: <TagsOutlined /> },
                { label: 'Concepto', value: 'codigo', icon: <AppstoreOutlined /> },
                { label: 'Fecha', value: 'fecha', icon: <CalendarOutlined /> },
                { label: 'Subag.', value: 'subagencia', icon: <GlobalOutlined /> },
              ]}
            />
          </Space>
        </Col>
      </Row>
    </Card>
  );

  // ============ RENDER ============
  return (
    <PageContainer
      title="Estadísticas de facturación"
    //   subtitle={isMobile
    //     ? `${kpis.numRegistros} registros`
    //     : `${kpis.numRegistros} registros · ${formatEuro(kpis.totalImporte)}`}
      icon={<FundOutlined />}
      loading={loading}
      onRefresh={fetchEstadisticas}
    //   badgeCount={kpis.numRegistros}
    //   badgeStatus="processing"
      extra={!isMobile && (
        <Button icon={<DownloadOutlined />} onClick={() => message.info('Exportando...')}>
          Exportar
        </Button>
      )}
      cardStyle={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
    >
      {/* ========== 🎯 FILTROS ARRIBA (AFECTAN A TODO) ========== */}
      {filtrosBar}

      {/* ========== KPIs ========== */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={12} md={6}>
          <KPICard
            title="Importe Total"
            value={formatEuro(kpis.totalImporte)}
            icon={<DollarOutlined />}
            color="#52c41a"
            subtitle="Facturación acumulada"
            isMobile={isMobile}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <KPICard
            title="Unidades"
            value={formatNumber(kpis.totalUnidades)}
            icon={<ShoppingOutlined />}
            color="#1890ff"
            subtitle="Servicios facturados"
            isMobile={isMobile}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <KPICard
            title="Factura media"
            value={formatEuro(kpis.ticketMedio)}
            icon={<PercentageOutlined />}
            color="#722ed1"
            subtitle="Importe / unidad"
            isMobile={isMobile}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <KPICard
            title="Agencias / Series"
            value={`${kpis.numAgencias} / ${kpis.numSeries}`}
            icon={<TeamOutlined />}
            color="#fa8c16"
            subtitle={`${kpis.numConceptos} conceptos`}
            isMobile={isMobile}
          />
        </Col>
      </Row>

      {/* ========== TOP CARDS ========== */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #faad14' }}
            styles={{ body: { padding: 14 } }}>
            <Space>
              <Avatar style={{ backgroundColor: '#faad14' }} icon={<TrophyOutlined />} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>Top Agencia</Text>
                <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {kpis.topAgencia?.agencia} - {kpis.topAgencia?.nombre}
                </div>
                <Text strong style={{ color: '#52c41a' }}>{formatEuro(kpis.topAgencia?.total)}</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #eb2f96' }}
            styles={{ body: { padding: 14 } }}>
            <Space>
              <Avatar style={{ backgroundColor: '#eb2f96' }} icon={<FireOutlined />} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>Top Concepto</Text>
                <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {kpis.topConcepto?.descripcion}
                </div>
                <Text strong style={{ color: '#52c41a' }}>{formatEuro(kpis.topConcepto?.total)}</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #13c2c2' }}
            styles={{ body: { padding: 14 } }}>
            <Space>
              <Avatar style={{ backgroundColor: '#13c2c2' }} icon={<ThunderboltOutlined />} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>Mejor Día</Text>
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {kpis.mejorDia?.fecha ? dayjs(kpis.mejorDia.fecha).format('DD MMM YYYY') : '-'}
                </div>
                <Text strong style={{ color: '#52c41a' }}>{formatEuro(kpis.mejorDia?.total)}</Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* ========== EVOLUCIÓN TEMPORAL ========== */}
      <Card
        title={
          <Space>
            <AreaChartOutlined style={{ color: '#1890ff' }} />
            <span>Evolución Temporal</span>
            <Tag color="blue">{chartData.porFecha.length} días</Tag>
          </Space>
        }
        style={{ marginBottom: 16, borderRadius: 12 }}
        size="small"
        styles={{ body: { padding: isMobile ? 8 : 16 } }}
      >
        {chartData.porFecha.length > 0 ? (
          <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
            <AreaChart data={chartData.porFecha}>
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#1890ff" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="colorUnidades" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#52c41a" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#52c41a" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="fecha" tick={{ fontSize: 11 }}
                tickFormatter={(v) => dayjs(v).format('DD/MM')} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              <RTooltip
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                labelFormatter={(v) => dayjs(v).format('DD MMMM YYYY')}
                formatter={(value, name) => name === 'Total'
                  ? [formatEuro(value), 'Importe']
                  : [formatNumber(value), 'Unidades']}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area yAxisId="left" type="monotone" dataKey="total" name="Total"
                stroke="#1890ff" strokeWidth={2} fill="url(#colorTotal)" />
              <Area yAxisId="right" type="monotone" dataKey="unidades" name="Unidades"
                stroke="#52c41a" strokeWidth={2} fill="url(#colorUnidades)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : <Empty description="Sin datos temporales" />}
      </Card>

      {/* ========== TOP AGENCIAS + PIE SERIES ========== */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={14}>
          <Card
            title={<Space><BarChartOutlined style={{ color: '#1890ff' }} /> Top Agencias por Importe</Space>}
            size="small"
            style={{ borderRadius: 12 }}
            styles={{ body: { padding: isMobile ? 8 : 16 } }}
          >
            {chartData.porAgencia.length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 350}>
                <BarChart
                  data={chartData.porAgencia.slice(0, 10)}
                  layout="vertical"
                  margin={{ left: isMobile ? 5 : 20, right: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={isMobile ? 45 : 60} />
                  <RTooltip
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                    formatter={(value, name, props) => [formatEuro(value), props.payload.fullName || props.payload.name]}
                  />
                  <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                    {chartData.porAgencia.slice(0, 10).map((entry, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty />}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title={<Space><PieChartOutlined style={{ color: '#eb2f96' }} /> Distribución por Serie</Space>}
            size="small"
            style={{ borderRadius: 12 }}
            styles={{ body: { padding: isMobile ? 8 : 16 } }}
          >
            {chartData.porSerie.length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 350}>
                <PieChart>
                  <Pie
                    data={chartData.porSerie}
                    dataKey="total"
                    nameKey="name"
                    cx="50%" cy="50%"
                    innerRadius={isMobile ? 40 : 60}
                    outerRadius={isMobile ? 80 : 110}
                    paddingAngle={4}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                    labelLine={{ stroke: '#d9d9d9' }}
                  >
                    {chartData.porSerie.map((entry, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <RTooltip
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                    formatter={(v) => formatEuro(v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <Empty />}
          </Card>
        </Col>
      </Row>

      {/* ========== TOP CONCEPTOS ========== */}
      <Card
        title={<Space><AppstoreOutlined style={{ color: '#722ed1' }} /> Top Conceptos por Importe</Space>}
        size="small"
        style={{ borderRadius: 12, marginBottom: 16 }}
        styles={{ body: { padding: isMobile ? 8 : 16 } }}
      >
        {chartData.porConcepto.length > 0 ? (
          <ResponsiveContainer width="100%" height={Math.min(chartData.porConcepto.length * 40 + 60, 500)}>
            <BarChart data={chartData.porConcepto.slice(0, 12)} layout="vertical"
              margin={{ left: isMobile ? 5 : 40, right: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }}
                width={isMobile ? 100 : 140} />
              <RTooltip
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                formatter={(v) => formatEuro(v)}
              />
              <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                {chartData.porConcepto.slice(0, 12).map((entry, i) => (
                  <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Card>

      {/* ========== COMPARATIVA IMPORTE vs UNIDADES ========== */}
      <Card
        title={<Space><DotChartOutlined style={{ color: '#13c2c2' }} /> Comparativa Importe vs Unidades</Space>}
        size="small"
        style={{ borderRadius: 12, marginBottom: 16 }}
        styles={{ body: { padding: isMobile ? 8 : 16 } }}
      >
        {chartData.porAgencia.length > 0 ? (
          <ResponsiveContainer width="100%" height={isMobile ? 260 : 320}>
            <ComposedChart data={chartData.porAgencia.slice(0, 8)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              <RTooltip
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                formatter={(value, name) => name === 'total'
                  ? [formatEuro(value), 'Importe']
                  : [formatNumber(value), 'Unidades']}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="total" name="Importe" fill="#1890ff" radius={[6, 6, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="unidades" name="Unidades"
                stroke="#fa8c16" strokeWidth={3} dot={{ r: 5, fill: '#fa8c16' }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Card>

      {/* ========== TREEMAP CONCEPTOS ========== */}
      <Card
        title={<Space><AppstoreOutlined style={{ color: '#722ed1' }} /> Mapa de Conceptos</Space>}
        size="small"
        style={{ borderRadius: 12, marginBottom: 16 }}
        styles={{ body: { padding: isMobile ? 8 : 16 } }}
      >
        {chartData.porConcepto.length > 0 ? (
          <ResponsiveContainer width="100%" height={isMobile ? 280 : 400}>
            <Treemap
              data={chartData.porConcepto.map(c => ({ ...c, size: Math.abs(c.total) }))}
              dataKey="size" aspectRatio={4 / 3} stroke="#fff" fill="#8884d8"
              content={({ x, y, width, height, name, value, index }) => {
                const color = COLORS[index % COLORS.length];
                return (
                  <g>
                    <rect x={x} y={y} width={width} height={height}
                      style={{ fill: color, stroke: '#fff', strokeWidth: 2, fillOpacity: 0.85 }} />
                    {width > 60 && height > 30 && (
                      <>
                        <text x={x + width / 2} y={y + height / 2 - 6}
                          textAnchor="middle" fill="#fff" fontSize={12} fontWeight="bold">
                          {name?.substring(0, 14)}
                        </text>
                        <text x={x + width / 2} y={y + height / 2 + 12}
                          textAnchor="middle" fill="#fff" fontSize={11}>
                          {formatEuro(value)}
                        </text>
                      </>
                    )}
                  </g>
                );
              }}
            >
              <RTooltip
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                formatter={(v, name, props) => [formatEuro(props.payload.total), props.payload.name]}
              />
            </Treemap>
          </ResponsiveContainer>
        ) : <Empty />}
      </Card>

      {/* ========== TABLA PRINCIPAL ========== */}
      <Card
        title={
          <Space>
            <BarChartOutlined style={{ color: '#722ed1' }} />
            <span>
              Desglose por {
                agrupacion === 'codigo' ? 'Concepto'
                  : agrupacion === 'agencia' ? 'Agencia'
                    : agrupacion === 'serie' ? 'Serie'
                      : agrupacion === 'subagencia' ? 'Subagencia' : 'Fecha'
              }
            </span>
            <Badge count={tableData.length} style={{ backgroundColor: '#722ed1' }} />
          </Space>
        }
        size="small"
        style={{ borderRadius: 12 }}
        styles={{ body: { padding: isMobile ? 0 : 16 } }}
      >
        <Table
          columns={tableColumns}
          dataSource={tableData}
          rowKey="key"
          loading={loading}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: isMobile ? 500 : 1000 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: !isMobile,
            showTotal: (t) => `${t} registros`,
          }}
          locale={{ emptyText: <Empty description="No hay datos con los filtros aplicados" /> }}
          summary={(pageData) => {
            const total = pageData.reduce((s, r) => s + r.total, 0);
            const unidades = pageData.reduce((s, r) => s + r.unidades, 0);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 700 }}>
                  <Table.Summary.Cell index={0}><Text strong>TOTAL PÁGINA</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong style={{ color: '#52c41a' }}>{formatEuro(total)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <Text strong>{formatNumber(unidades)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} />
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      {isMobile && (
        <FloatButton
          icon={<FilterOutlined />}
          type="primary"
          style={{ right: 16, bottom: 80 }}
          onClick={() => setFiltrosDrawerVisible(true)}
          tooltip="Filtros"
        />
      )}
    </PageContainer>
  );
};

export default EstadisticasFacturacion;